import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutGrid } from 'lucide-react';
import SignedImage from '@/components/ipe/SignedImage';
import ImageTemplatePickerModal from '@/pages/ipe/workbench/modals/ImageTemplatePickerModal';
import PodBatchDesignFlowIntro from '@/pages/ipe/workbench/components/PodBatchDesignFlowIntro';
import {
  loadImageTemplatePreviewUrls,
  resolveImageTemplatePreviewUrl,
} from '@/pages/ipe/workbench/utils/templateListUtils';
import '@/styles/ipe/workbench/Img2ImgPage.css';
import '@/styles/ipe/workbench/PodBatchComposePage.css';

function TemplatePreviewCard({ template, previewUrl, onRemove, readOnly }) {
  const { t } = useTranslation('workbench');
  const name = template.name || template.id;
  return (
    <article className="pod-batch__template-card">
      <div className="pod-batch__template-card-preview">
        {previewUrl ? (
          <SignedImage
            url={previewUrl}
            displayUrl={template.previewUrlDisplay || template.viewUrl || template.displayUrl}
            viewUrl={template.viewUrl || template.previewUrlDisplay}
            alt={template.name || t('workflowParams.previewAlt')}
            loading="lazy"
          />
        ) : (
          <span className="pod-batch__template-card-placeholder">{t('workflowParams.noPreview')}</span>
        )}
      </div>
      <p className="pod-batch__template-card-name" title={template.name}>
        {name}
      </p>
      {!readOnly && onRemove ? (
        <button
          type="button"
          className="pod-batch__template-card-remove"
          onClick={() => onRemove(template)}
          aria-label={t('workflowParams.removeTpl', { name })}
        >
          ×
        </button>
      ) : null}
    </article>
  );
}

/** POD 商品设计：与 {@link PodBatchComposePage} 侧栏/工作区一致的模板选择 UI */
export default function PodProductDesignStepParams({
  p,
  onChange,
  imageTemplates,
  catalogsLoading,
  readOnly = false,
  showFlowIntro = false,
}) {
  const { t } = useTranslation('workbench');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerGroup, setPickerGroup] = useState('全部');
  const [previewUrlMap, setPreviewUrlMap] = useState({});

  // 不能直接依赖 p.imageTemplateIds 派生的数组：每次渲染都是新引用，
  // 会让下面的 useMemo 失效并把 selectedTemplates 变成新数组，
  // 进而使预览 useEffect 在「每次渲染」都重新执行、反复 setState → 页面卡死。
  // 因此先归一到字符串 key，再按 key 记忆化。
  const selectedIdKey = (p.imageTemplateIds || []).map(String).join(',');
  const selectedIds = useMemo(
    () => (selectedIdKey ? selectedIdKey.split(',') : []),
    [selectedIdKey],
  );

  const scopedTemplates = useMemo(
    () => (Array.isArray(imageTemplates) ? imageTemplates : []),
    [imageTemplates],
  );

  const templateGroups = useMemo(
    () => Array.from(new Set(scopedTemplates.map((t) => t?.group).filter(Boolean))),
    [scopedTemplates],
  );

  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const selectedTemplates = useMemo(
    () => scopedTemplates.filter((t) => selectedIdSet.has(String(t.id))),
    [scopedTemplates, selectedIdSet],
  );

  const showEmptyFlowIntro = showFlowIntro && selectedTemplates.length === 0;

  useEffect(() => {
    if (!selectedTemplates.length) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const urlMap = await loadImageTemplatePreviewUrls(selectedTemplates);
        if (cancelled || Object.keys(urlMap).length === 0) return;
        setPreviewUrlMap((prev) => {
          // 只在确有新增/变化时更新，避免相同数据反复触发渲染
          const changed = Object.keys(urlMap).some((key) => prev[key] !== urlMap[key]);
          return changed ? { ...prev, ...urlMap } : prev;
        });
      } catch (error) {
        console.warn('加载 POD 模板预览失败', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedTemplates]);

  const handleToggleTemplate = (tpl) => {
    if (!tpl?.id || readOnly) return;
    const id = String(tpl.id);
    const set = new Set(selectedIds);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    onChange({ imageTemplateIds: [...set] });
  };

  const handleRemoveTemplate = (tpl) => {
    if (!tpl?.id || readOnly) return;
    const id = String(tpl.id);
    onChange({ imageTemplateIds: selectedIds.filter((x) => x !== id) });
  };

  const handleConfirmPicker = () => {
    if (selectedIds.length === 0) return;
    setPickerOpen(false);
  };

  const openPicker = () => {
    if (readOnly || catalogsLoading) return;
    setPickerOpen(true);
  };

  const selectTemplateButton = (
    <button
      type="button"
      className="img2img-studio__upload-btn"
      onClick={openPicker}
      disabled={readOnly || catalogsLoading}
    >
      <LayoutGrid size={16} aria-hidden="true" />
      {catalogsLoading ? t('workflowParams.loadingList') : t('workflowParams.pickTemplates')}
    </button>
  );

  return (
    <div className="pod-product-design-step-params wf-studio-params-root">
      {showEmptyFlowIntro ? (
        <PodBatchDesignFlowIntro
          className="pod-product-design-step-params__flow-intro"
          selectButton={selectTemplateButton}
          hintExtra={
            !readOnly && !catalogsLoading ? (
              <p className="pod-product-design-step-params__hint-warn">{t('workflowParams.needTemplate')}</p>
            ) : null
          }
        />
      ) : (
        <>
          <section className="pod-batch__section" aria-label={t('workflowParams.designTemplates')}>
            <span className="img2img-studio__field-label">{t('workflowParams.designTemplates')}</span>
            <div className="pod-batch__intro-actions pod-product-design-step-params__picker-row">
              {selectTemplateButton}
            </div>

            {selectedTemplates.length === 0 ? (
              <div className="img2img-studio__upload-hint pod-product-design-step-params__hint">
                <p>{t('workflowParams.multiHint')}</p>
                {!readOnly && !catalogsLoading ? (
                  <p className="pod-product-design-step-params__hint-warn">{t('workflowParams.needTemplate')}</p>
                ) : null}
              </div>
            ) : (
              <div className="pod-batch__template-grid pod-product-design-step-params__grid">
                {selectedTemplates.map((tpl) => (
                  <TemplatePreviewCard
                    key={String(tpl.id)}
                    template={tpl}
                    previewUrl={resolveImageTemplatePreviewUrl(tpl, previewUrlMap)}
                    onRemove={handleRemoveTemplate}
                    readOnly={readOnly}
                  />
                ))}
              </div>
            )}
          </section>
        </>
      )}

      {/* 弹窗置于单一树内，避免空态/已选分支切换时 portal 卸载再挂载 */}
      <ImageTemplatePickerModal
        isOpen={pickerOpen}
        title={t('workflowParams.pickTitle')}
        onCancel={() => setPickerOpen(false)}
        onConfirm={handleConfirmPicker}
        groups={templateGroups}
        selectedGroup={pickerGroup}
        onGroupChange={setPickerGroup}
        templates={scopedTemplates}
        selectedTemplateIds={selectedIds}
        onToggleTemplate={handleToggleTemplate}
        confirming={readOnly}
        modalClassName="funding-modal--wf-template-picker"
      />
    </div>
  );
}
